import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { T, TYPE, primaryButtonSx, ghostButtonSx } from "../../user/complaints/shared";
import { pm, errorMessage, errorDetails } from "../api";
import { usePm } from "../PmContext";
import { Select, fieldSx } from "../inputs";

const toDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

/**
 * mode: create | edit | start
 * `board` is required for create (sprints belong to a board).
 */
export const SprintFormDialog = ({ state, board, project, onClose, onDone }) => {
  const ctx = usePm();
  const [f, setF] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!state) return;
    const s = state.sprint || {};
    const start = s.startDate ? new Date(s.startDate) : new Date();
    const end = s.endDate ? new Date(s.endDate) : new Date(start.getTime() + (project.defaultSprintDays || 14) * 86400000);
    setF({ name: s.name || "", goal: s.goal || "", description: s.description || "", startDate: toDate(start), endDate: toDate(end) });
    setError("");
  }, [state, project.defaultSprintDays]);
  if (!state || !f) return null;
  const { mode, sprint } = state;
  const rules = board?.settings?.sprintStart || {};
  const requirements = [rules.requireIssues && "at least one issue", rules.requireGoal && "a goal", rules.requireDates && "dates", rules.enforceCapacity && "fits capacity"].filter(Boolean);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { name: f.name, goal: f.goal, description: f.description, startDate: f.startDate || null, endDate: f.endDate || null };
      if (mode === "create") await pm.createBoardSprint(board._id, body);
      else if (mode === "edit") await pm.updateSprint(sprint._id, body);
      else await pm.startSprint(sprint._id, { goal: f.goal, startDate: f.startDate, endDate: f.endDate });
      ctx.notify({ create: "Sprint created", edit: "Sprint saved", start: `${sprint?.name} started` }[mode]);
      onDone();
    } catch (err) {
      const list = errorDetails(err);
      setError(Array.isArray(list) && list.length > 1 ? list.join(" · ") : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{{ create: "Create sprint", edit: `Edit ${sprint?.name}`, start: `Start ${sprint?.name}` }[mode]}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          {mode !== "start" && <TextField size="small" label="Name" placeholder={`${board?.key || project.key} Sprint`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} sx={fieldSx} />}
          <TextField size="small" multiline minRows={2} label={`Sprint goal${rules.requireGoal ? " *" : ""}`} value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} sx={fieldSx} />
          {mode !== "start" && <TextField size="small" multiline minRows={2} label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} sx={fieldSx} />}
          <Stack direction="row" spacing={2}>
            <TextField fullWidth size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} sx={fieldSx} />
            <TextField fullWidth size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={f.endDate} onChange={(e) => setF({ ...f, endDate: e.target.value })} sx={fieldSx} />
          </Stack>
          {mode === "start" && (
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>
              The current scope becomes the sprint commitment (burndown, velocity).
              {requirements.length > 0 && ` Board rules: ${requirements.join(", ")}.`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={busy} onClick={submit}>{{ create: "Create", edit: "Save", start: "Start sprint" }[mode]}</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Complete sprint: shows done vs not done from the server, asks where unfinished work goes. */
export const CompleteSprintDialog = ({ sprint, onClose, onDone }) => {
  const ctx = usePm();
  const [preview, setPreview] = useState(null);
  const [moveTo, setMoveTo] = useState("backlog");
  const [target, setTarget] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!sprint) return;
    setPreview(null);
    setError("");
    pm.sprintCompletion(sprint._id).then((p) => {
      setPreview(p);
      setMoveTo(p.defaultMoveTo === "next" ? "next" : p.defaultMoveTo === "new" ? "new" : "backlog");
      setTarget(p.futureSprints[0]?._id || "");
    }).catch((err) => setError(errorMessage(err)));
  }, [sprint]);
  if (!sprint) return null;
  const complete = async () => {
    setBusy(true);
    try {
      const r = await pm.completeSprint(sprint._id, { moveTo: moveTo === "existing" ? target : moveTo });
      ctx.notify(`${sprint.name} completed${r.movedTo ? ` · unfinished work moved to ${r.movedTo.name}` : ""}`);
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Complete {sprint.name}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!preview ? <CircularProgress size={20} /> : (
          <>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: "#DCFAE6" }}><Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, color: "#067647" }}>COMPLETED</Typography><Typography sx={{ fontWeight: 700 }}>{preview.completed.count} issues · {preview.completed.points} pts</Typography></Box>
              <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: "#FEF0C7" }}><Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, color: "#B54708" }}>INCOMPLETE</Typography><Typography sx={{ fontWeight: 700 }}>{preview.incomplete.count} issues · {preview.incomplete.points} pts</Typography></Box>
            </Box>
            {preview.incomplete.count > 0 ? (
              <>
                <Typography sx={{ fontSize: TYPE.small, fontWeight: 600, mb: 0.5 }}>Move incomplete issues to</Typography>
                <RadioGroup value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                  <FormControlLabel value="backlog" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>Backlog</Typography>} />
                  <FormControlLabel value="next" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>Next sprint{preview.futureSprints[0] ? ` (${preview.futureSprints[0].name})` : " (a new one is created)"}</Typography>} />
                  {preview.futureSprints.length > 1 && <FormControlLabel value="existing" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>Another future sprint</Typography>} />}
                  <FormControlLabel value="new" control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>A new sprint</Typography>} />
                </RadioGroup>
                {moveTo === "existing" && <Select label="Sprint" value={target} onChange={setTarget} options={preview.futureSprints.map((s) => ({ value: s._id, label: s.name }))} />}
                <Box sx={{ mt: 1.5, maxHeight: 160, overflowY: "auto" }}>
                  {preview.incomplete.issues.map((i) => <Typography key={i._id} sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}><b>{i.key}</b> {i.summary}</Typography>)}
                </Box>
              </>
            ) : <Alert severity="success">Everything in this sprint is done.</Alert>}
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted, mt: 1.5 }}>Completed work is recorded for velocity; the burndown is finalised. Nothing is deleted or duplicated.</Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={!preview || busy || (moveTo === "existing" && !target)} onClick={complete}>Complete sprint</Button>
      </DialogActions>
    </Dialog>
  );
};

export const CancelSprintDialog = ({ sprint, onClose, onDone }) => {
  const ctx = usePm();
  const [reason, setReason] = useState("");
  if (!sprint) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Cancel {sprint.name}?</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: TYPE.small, mb: 1.5 }}>Unfinished issues go back to the backlog. A cancelled sprint never counts for velocity.</Typography>
        <TextField fullWidth size="small" label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} sx={fieldSx} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep sprint</Button>
        <Button variant="contained" color="error" onClick={async () => { try { const r = await pm.cancelSprint(sprint._id, { reason }); ctx.notify(`${sprint.name} cancelled · ${r.movedToBacklog} issue(s) back to the backlog`); onDone(); } catch (err) { ctx.fail(err); } }}>Cancel sprint</Button>
      </DialogActions>
    </Dialog>
  );
};
